import { useId, useReducer } from "react";
import { useIntl } from "react-intl";
import { AiDataCaptureCaption } from "../AiDataCaptureCaption";
import type { TablePayload } from "../ai-chat-message-utils";

interface AiTableProps {
  payload: TablePayload;
}

type SortDir = "asc" | "desc";

interface SortState {
  column: string | null;
  dir: SortDir;
}

type SortAction = { type: "sort"; column: string };

const sortReducer = (state: SortState, action: SortAction): SortState => {
  if (action.column === state.column) {
    return { ...state, dir: state.dir === "asc" ? "desc" : "asc" };
  }
  return { column: action.column, dir: "asc" };
};

type SortIconState = "none" | "ascending" | "descending";

const SORT_ICON_PATHS: Record<SortIconState, string> = {
  none: "M9,3L5,7H8V14H10V7H13M16,17V10H14V17H11L15,21L19,17H16Z",
  ascending: "M5 9L6.41 10.41L11 5.83V22H13V5.83L17.59 10.42L19 9L12 2L5 9Z",
  descending: "M19 15L17.59 13.59L13 18.17V2H11V18.17L6.41 13.58L5 15L12 22L19 15Z",
};

const SortIcon = ({ state }: { state: SortIconState }) => (
  <svg className="sort-button__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d={SORT_ICON_PATHS[state]} fill="currentColor" />
  </svg>
);


export const AiTable = ({ payload }: AiTableProps) => {
  const intl = useIntl();
  const { columns, rows, title } = payload;
  const titleId = useId();

  const [{ column: sortColumn, dir: sortDir }, dispatch] = useReducer(
    sortReducer,
    { column: columns[0] ?? null, dir: "asc" },
  );

  const sortColumnIndex = sortColumn !== null ? columns.indexOf(sortColumn) : -1;

  const sortedRows =
    sortColumnIndex >= 0
      ? [...rows].sort((firstRow, secondRow) => {
          const firstCellValue = firstRow[sortColumnIndex];
          const secondCellValue = secondRow[sortColumnIndex];
          const comparisonResult =
            typeof firstCellValue === "number" && typeof secondCellValue === "number"
              ? (firstCellValue < secondCellValue ? -1 :
                 firstCellValue > secondCellValue ? 1 : 0)
              : String(firstCellValue).localeCompare(String(secondCellValue), undefined, {
                  numeric: true,
                });
          return sortDir === "asc" ? comparisonResult : -comparisonResult;
        })
      : rows;

  const sortAnnouncement = sortColumn
    ? intl.formatMessage(
        {
          id:
            sortDir === "asc"
              ? "app.toolboxChat.table.sortAscending"
              : "app.toolboxChat.table.sortDescending",
        },
        { column: sortColumn },
      )
    : "";
  return (
    <div className="paper-card tcw-table-card">
      <div className="header tcw-table-header">
        {title && (
          <h3 id={titleId} className="title">
            {title}
          </h3>
        )}
      </div>
      <div className="body">
        <table className="data-table" aria-labelledby={title ? titleId : undefined}>
          <thead>
            <tr>
              {columns.map((col) => {
                const isSorted = col === sortColumn;
                const sortState: SortIconState = isSorted
                  ? sortDir === "asc"
                    ? "ascending"
                    : "descending"
                  : "none";
                return (
                  <th
                    key={col}
                    scope="col"
                    className="dataheadercell sortable"
                    aria-sort={isSorted ? sortState : undefined}
                  >
                    <button
                      type="button"
                      className="sort-button"
                      onClick={() => dispatch({ type: "sort", column: col })}
                    >
                      {col}
                      <SortIcon state={sortState} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="datacell" data-th={columns[cellIndex]}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sr-only" aria-live="polite">
          {sortAnnouncement}
        </div>
      </div>
        <AiDataCaptureCaption />
    </div>
  );
};
